
import React, { useState, useEffect } from 'react';
import type { VacancyFlowStepHistoryEntry } from './utils/vacancyFlowHistory';
import type { VacancyIntakeVersionEntry } from './utils/vacancyIntakeVersions';

export enum UserRole {
  ADMIN = 'ADMIN',
  CLIENT = 'CLIENT',
  RECRUITER = 'RECRUITER',
  CLIENT_RECRUITER = 'CLIENT_RECRUITER',
  VACANCY_REVIEWER = 'VACANCY_REVIEWER'
}

export interface AccessProfile {
  id: string;
  name: string;
  role: UserRole;
  visibleTabs: string[];
  description?: string;
}
export interface User {
  id: string;
  name: string;
  role: UserRole;
  email: string;
  phone?: string;
  credits: number;
  avatar?: string;
  clientId?: string;
  clientName?: string;
  /** Klantportaal: logo van CRM-organisatie (`clients.logo_url`), o.a. vanuit `client_contacts.linked_client_id`. */
  clientLogoUrl?: string;
  /** Klantportaal: false = alleen AIM (geen shortlist-fase kandidaten); overige klanten true. */
  portalFullPipelineVisibility?: boolean;
  branding?: {
    logoUrl?: string;
    primaryColor?: string;
    secondaryColor?: string;
    aiRecruiter?: {
      name: string;
      description: string;
      avatarUrl?: string;
    };
  };
  visibleTabs?: string[];
  /** Persoonlijk verborgen hoofdmenu-items (id zoals in Layout: `reporting`, `talentpool`, …). Opgeslagen in `profiles.data.sidebar_hidden_ids`. */
  sidebarHiddenIds?: string[];
  /** Voorkeursvolgorde van hoofdmenu-apps (zonder `dashboard`). Opgeslagen in `profiles.data.sidebar_order_ids`. */
  sidebarOrderIds?: string[];
  recruiterProfile?: RecruiterProfile; // Linked profile for recruiters
  tokensInput?: number; // AI usage tracker
  tokensOutput?: number; // AI usage tracker
}

export interface RecruiterProfile {
  capacity: {
    current: number;
    max: number;
  };
  skills: string[];
  performance: {
    placementRate: number;
    avgDaysToFill: number;
    slaCompliance: number;
  };
  status: 'Available' | 'Busy' | 'Offline';
}

export type SLAStatus = 'OnTrack' | 'Warning' | 'Breach';

export type TriggerType = 'application_created' | 'status_rejected' | 'status_interview' | 'candidate_birthday' | 'vacancy_published' | 'sla_overdue' | 'candidate_hired' | 'manual_trigger' | 'positive_review' | 'stage_changed' | 'new_note' | 'custom_event' | 'candidate_created' | 'candidate_status_changed' | 'contract_created' | 'contract_expiring' | 'vacancy_closed' | 'meeting_scheduled';
export interface AutomationTriggerConfig {
  customEventName?: string;
  targetStageName?: string;
}

export interface AutomationCondition {
  field: string;
  operator: 'equals' | 'contains' | 'greater_than' | 'less_than' | 'is_set' | 'is_not_set';
  value: string;
}

export interface AutomationConditionGroup {
  id: string;
  type: 'and' | 'or';
  conditions: AutomationCondition[];
}

export interface AutomationBranch {
  id: string;
  name: string;
  conditionGroups: AutomationConditionGroup[];
  steps: AutomationStep[];
}

export type AutomationActionType = 'email' | 'sms' | 'whatsapp' | 'wait' | 'notification' | 'stage_change' | 'add_tag' | 'create_task' | 'internal_note' | 'ai_score' | 'condition' | 'branch' | 'docusign' | 'n8n_webhook';

export interface AutomationFlow {
  id: string;
  name: string;
  description?: string;
  trigger: TriggerType;
  trigger_config?: AutomationTriggerConfig;
  status: 'active' | 'draft' | 'paused';
  scope: 'global' | 'partner' | 'client'; // Governance
  steps: AutomationStep[];
  client_id?: string;
  tags?: string[];
  stats?: {
    runs: number;
    success_rate: number;
    last_run?: string;
  };
}

export interface AutomationStepConfig {
  templateId?: string;
  delay?: number;
  delayUnit?: 'minutes' | 'hours' | 'days';
  from?: string;
  to?: string;
  subject?: string;
  smsType?: 'custom' | 'template';
  customMessage?: string;
  targetStage?: string;
  tagToAdd?: string;
  taskTitle?: string;
  taskPriority?: 'Low' | 'Medium' | 'High';
  taskAssignee?: string;
  noteContent?: string;
  logicAction?: 'continue' | 'stop';
  conditionField?: string;
  conditionOperator?: string;
  conditionValue?: string;
  sendMode?: 'auto' | 'manual';
  branches?: AutomationBranch[];
  webhookUrl?: string;
  webhookPayload?: string;
}

export interface AutomationStep {
  id: string;
  type: AutomationActionType;
  label: string;
  config: AutomationStepConfig;
}

export interface AutomationTemplate {
  id: string;
  name: string;
  type: 'Email' | 'SMS' | 'WhatsApp' | 'Section';
  subject?: string;
  content: string;
  updatedAt: string;
  client_id?: string;
  variables?: string[];
}

export interface FlowExecutionLog {
  id: string;
  flow_id?: string;
  flowName: string;
  triggerEvent: string;
  timestamp: string;
  status: 'success' | 'failed' | 'running';
  stepCount: number;
  details?: string;
}

// --- WEBSITE CONTENT TYPES ---
export type WebsiteSectionType = 'hero' | 'features' | 'pillars' | 'partners' | 'cta' | 'pricing' | 'text_image' | 'contact' | 'about' | 'faq' | 'testimonials' | 'logos' | 'vacancies' | 'team' | 'gallery' | 'custom' | 'video' | 'stats' | 'text_only' | 'footer';

export interface SectionLayout {
  containerType: 'boxed' | 'full-width';
  columns: 1 | 2 | 3 | 4;
  columnRatios: string[]; // e.g. [50, 50]
  verticalAlignment: 'top' | 'middle' | 'bottom';
  gap?: 'none' | 'small' | 'medium' | 'large';
}

export interface SectionElement {
  id: string;
  type: 'heading' | 'text' | 'rich_text' | 'image' | 'icon' | 'icon_list' | 'button' | 'divider' | 'spacer' | 'card' | 'grid' | 'repeater' | 'vacancy_list' | 'testimonial' | 'logo_cloud' | 'stats_counter';
  content: any;
  columnIndex?: number; // 0-indexed column assignment
  style?: {
    typography?: any;
    colors?: any;
    spacing?: any;
    borders?: any;
    responsive?: {
      mobile?: { display: 'block' | 'none', width?: string };
      tablet?: { display: 'block' | 'none', width?: string };
      desktop?: { display: 'block' | 'none', width?: string };
    };
  };
}

export interface SectionResponsiveRules {
  mobile: { stackColumns: boolean };
  tablet: { stackColumns: boolean };
}

export interface WebsiteSection {
  id: string;
  type: WebsiteSectionType;
  title: string;
  subtitle?: string;
  content?: any;
  styles?: {
    backgroundColor?: string;
    textColor?: string;
    buttonColor?: string; // Custom CTA button color
    buttonTextColor?: string; // Custom CTA text color
    padding?: 'small' | 'normal' | 'large'; // Extra breathing room control
    align?: 'left' | 'center' | 'right'; // Content alignment
    fontFamily?: string;
    fontSize?: string;
    backgroundImage?: string;
    fontSizeTitle?: string;
    imagePosition?: 'left' | 'right';
    textShadow?: string;
    subtitleStyles?: {
      fontFamily?: string;
      fontSize?: string;
      color?: string;
    };
    borderRadius?: 'sharp' | 'rounded' | 'playful';
    shadow?: 'none' | 'subtle' | 'prominent';
  };
  isVisible: boolean;

  // New Advanced Structure
  metadata?: {
    name: string;
    category: string;
    purpose: string;
  };
  layout?: SectionLayout;
  elements?: SectionElement[];
  responsiveRules?: SectionResponsiveRules;
  dynamicFields?: string[]; // e.g. ['{{company_name}}']
}

export interface WebsiteConfig {
  logoUrl?: string;
  primaryColor: string;
  fontFamily: string;
  pages: Record<string, WebsiteSection[]>;
  seo?: {
    title?: string;
    description?: string;
    faviconUrl?: string;
  };
}

export interface StepGovernance {
  visibility: 'everyone' | 'recruiters' | 'werfplaats';
  mode: 'results' | 'full';
  responsibility: 'Klant' | 'Werkplaats' | 'Gedeeld';
}

export interface Client {
  id: string;
  name: string;
  parent_client_id?: string;
  logo_url?: string;
  industry?: string;
  website?: string;
  phone?: string;
  email?: string;
  contact_person?: string;
  client_type: 'Direct' | 'Agency' | 'Company' | 'Subcontractor';
  is_businesspartner?: boolean;
  is_vacaturehouder?: boolean;
  is_opdrachtgever?: boolean;
  is_eindklant?: boolean;
  is_leverancier?: boolean;
  organizationDescription?: string; // Standard organization text
  address_street?: string;
  address_city?: string;
  address_zip?: string;
  invoice_kvk?: string;
  invoice_vat?: string;
  invoice_email?: string;

  internal_notes?: string;
  price_per_user?: number;
  custom_domain?: string;
  clientportal_custom_domain?: string;
  active_clientportal?: boolean;
  active_site_editor?: boolean;

  default_access_profile_id?: string;
  created_at?: string;
  ai_matching_settings?: AiMatchingSettings;
  contract_options?: string[];
  vacancyStructure?: string[]; // Custom vacancy structure

  // Plan & Limits
  plan_tier?: 'None' | 'Basic' | 'Pro' | 'Enterprise';
  billing_status?: 'trial' | 'active' | 'past_due' | 'canceled';
  period_start?: string;
  period_end?: string;
  hires_limit?: number;
  interviews_limit?: number;
  searches_limit?: number;
  reset_cycle?: 'monthly' | 'yearly' | 'never';
  overage_policy?: 'block' | 'allow_and_bill' | 'allow_with_warning';

  // Feature Flags
  feature_candidate_search?: boolean;
  feature_interview_flow?: boolean;
  feature_hiring_pipeline?: boolean;
  feature_external_recruitment_services?: boolean;

  // Integrations
  bullhorn_settings?: {
    clientId?: string;
    clientSecret?: string;
    apiUsername?: string;
    apiPassword?: string;
  };
  word_template_url?: string;
}

export interface ClientNote {
  id: string;
  client_id: string;
  note_text: string;
  created_at: string;
  created_by?: string;
  author_name?: string;
}

export interface AiMatchingSettings {
  customPrompt?: string;
  rejectionReasons?: string[];
  weights: {
    hardSkills: number; // 0-100
    softSkills: number;
    experience: number;
    culturalFit: number;
  };
  /** Legacy values may exist in DB; server uses Azure deployment from env */
  model?: string;
}

export interface Page {
  id: string;
  title: string;
  slug: string;
  sections?: WebsiteSection[];
  isHome?: boolean;
  seoDescription?: string;
  seoTitle?: string;
  craftState?: string;
  puckData?: any;
}

export interface BrandingConfig {
  companyName?: string;
  primaryColor: string;
  secondaryColor: string;
  palette?: {
    primary: string;
    sub1: string;
    sub2: string;
    sub3: string;
    sub4: string;
  };
  headerColor?: string;
  headerTextColor?: string;
  headerFontSize?: string;
  headerFont?: string;
  logoPadding?: number;
  footerColor?: string;
  footerTextColor?: string;
  footerFontSize?: string;
  footerFont?: string;
  fontColor?: string;
  font: string;
  logoUrl: string;
  heroImage: string;
  heroTitle: string;
  heroSubtitle: string;
  aboutTitle: string;
  aboutText: string;
  aboutImage?: string;
  stories: Story[];
  teamMembers: CareerTeamMember[];
  pages?: Page[];
  sections?: WebsiteSection[]; // Legacy/Home compatibility
  cultureTitle?: string;
  cultureItems: { icon: string; title: string; text: string }[];
  socialLinks?: { platform: 'linkedin' | 'instagram' | 'facebook' | 'twitter' | 'youtube' | 'website'; url: string }[];
  customDomain?: string;
  domainStatus?: 'none' | 'pending' | 'active' | 'error';
  dnsRecords?: { type: string; name: string; value: string; status: 'active' | 'pending' }[];
  lastChecked?: string;
  // Governance Settings
  stepGovernance?: Record<string, StepGovernance>;

  // Dynamic CMS
  headerConfig?: {
    customLinks?: { label: string; url: string }[];
    backgroundType?: 'solid' | 'transparent' | 'blur';
    showSocials?: boolean;
    ctaButton?: { label: string; url: string; color: string };
  };
  footerConfig?: {
    showSocials?: boolean;
    columns?: { title: string; links: { label: string; url: string }[] }[];
    copyrightText?: string;
    navTitle?: string;
    contactTitle?: string;
    socialTitle?: string;
  };
  consentTexts?: {
    privacyPolicy?: string;
    termsOfService?: string;
  };
  vacancyButtonColor?: string;
  linkedinButtonColor?: string;
}

export interface Story {
  id: string;
  title: string;
  employeeName: string;
  role: string;
  quote: string;
  imageUrl: string;
  content: string;
}

export interface CareerTeamMember {
  id: string;
  name: string;
  role: string;
  imageUrl: string;
  linkedin?: string;
  description?: string;
}

export interface CVWorkExperience {
  company: string;
  role: string;
  period: string;
  description: string;
  isList?: boolean;
  listValues?: string[];
}

export interface CVEducation {
  school: string;
  degree: string;
  year: string;
}

export interface CVData {
  id?: string;
  candidateId?: string;
  title: string;
  language?: string; // e.g. 'nl', 'en'
  summary: string;
  personalInfo: {
    name: string;
    email: string;
    phone: string;
    location: string;
    address?: string;
    zipCode?: string;
    city?: string;
    country?: string;
    preferredLanguage?: string;
    dateOfBirth?: string;
    placeOfBirth?: string;
    linkedin?: string;
    photoUrl?: string;
  };
  workHistory: CVWorkExperience[];
  education: CVEducation[];
  skills: any[];
  motivation?: string;
  references?: Array<{ name: string; role: string; company: string; email?: string; phone?: string }>;
  languages?: Array<{ language: string; level: string }>;
  courses?: Array<{ name: string; institute: string; year: string }>;
  hobbies?: string[];
  otherInfo?: string;
  customFields?: Array<{ id: string; name: string; value: string; label?: string; tag?: string, isList?: boolean, listValues?: string[] }>;
  shortcodes?: Array<{ name: string; value: string }>;
  eisen?: any[];
  wensen?: any[];
  course?: any[]; // New dynamic array for Courses block
}

export type CVSectionType = 'header' | 'summary' | 'experience' | 'education' | 'skills' | 'languages' | 'courses' | 'course' | 'certificates' | 'references' | 'hobbies' | 'soft_skills' | 'website' | 'privacy' | 'custom_list' | 'custom_text' | 'spacer' | 'columns' | 'image';

export interface CVSection {
  id: string;
  type: CVSectionType;
  title?: string;
  content?: any;
  styles?: React.CSSProperties;
  settings?: any; // Added for flexible configuration (e.g. hide email, reverse order)
  width?: number;
  columns?: CVSection[];
}

export interface CVDecoration {
  id: string;
  type: 'circle' | 'square' | 'line' | 'image' | 'text_box' | 'triangle' | 'rounded' | 'text';
  x: number;
  y: number;
  width: number;
  height: number;
  color?: string;
  imageUrl?: string;
  opacity: number;
  zIndex: number;
  rotation?: number;
  pageIndex: number;
  allPages?: boolean;
  text?: string;
  fontSize?: number;
  fontWeight?: string;
  fontStyle?: string;
  textDecoration?: string;
  textAlign?: 'left' | 'center' | 'right';
  fontFamily?: string;
  letterSpacing?: number;
  lineHeight?: number;
}

export interface CVDesignConfig {
  fontFamily: string;
  primaryColor: string;
  secondaryColor?: string;
  textColor: string;
  backgroundColor: string;
  backgroundImage?: string;
  backgroundOverlayColor?: string;
  backgroundOpacity?: number;
  pagePadding: number; // Keep for backward compatibility
  paddingTop?: number;
  paddingBottom?: number;
  paddingLeft?: number;
  paddingRight?: number;
  itemSpacing: number;
  fontSizeBody: number;
  fontSizeHeading: number;
  headerStyle?: 'standard' | 'sidebar' | 'banner';
  bulletColor?: string;
  tagColor?: string;
  tagTextColor?: string;
  layoutVariant?: 'classic' | 'modern' | 'compact';
}

export interface CVTemplate {
  id: string;
  name: string;
  clientId?: string;
  previewUrl?: string;
  type?: string;
  thumbnailUrl?: string;
  design: CVDesignConfig;
  structure: CVSection[][];
  decorations?: CVDecoration[];
  created_at?: string;
}

export interface TemplateItem {
  content: string;
  isTopThree: boolean;
  froukjeReason?: string;
  rank: number;
}

export interface TemplatePool {
  items: TemplateItem[];
  selectedIndex: number;
}

export interface VacancyTemplates {
  jobText: TemplatePool;
  linkedinPost: TemplatePool;
  inMail: TemplatePool;
  whatsappMessage: TemplatePool;
}

export interface MarketInsight {
  title: string;
  value: string;
  subtitle: string;
  trend?: string;
}

export interface ABTestVariant {
  id: string;
  name: string;
  content: string;
  views: number;
  applications: number;
  conversionRate: number;
  isWinner?: boolean;
}

export interface PersonaConfig {
  description: string;
  competencies: string[]; // Competenties
  personalityType: string; // Type persoon
  toneOfVoice: string;
  pullFactors: string[];
  salaryRange: string;
  secondaryBenefits: string;
  jobTitleSynonyms?: string[];
  steppingStoneRoles?: string[];
}

export interface MarketingConfig {
  targetingTitles: string[];
  feederRoles: string[]; // Opstap functies
  education: string[];
  skills: string[];
  competitors: string[]; // Arbeidsmarkt: Interessante concurrenten
  relatedIndustries: string[]; // Aanverwante industrieën
  highTurnoverCompanies: string[]; // Bedrijven met hoog verloop
}

export interface SourcingStrategy {
  platform: string;
  query: string;
  explanation: string;
  url?: string;
  targetPersona?: {
    bio: string;
    skills: string[];
    seniorityLevel: number; // 0-100
    seniorityLabel: string; // Junior, Medior, Senior, Lead
    educationLevel: string;
    currentJobTitles: string[];
    keywords: string[];
  };
}

export interface PremiumChannelConfig {
  selected: boolean;
  status?: 'Aangevraagd' | 'Online' | 'Afgerond';
  activeLink?: string;
  invoiceNumber?: string;
  invoiceDate?: string;
}

export interface PremiumJobboardsData {
  linkedin?: PremiumChannelConfig;
  indeed?: PremiumChannelConfig & { tier: 'sponsored' | 'premium'; endDate: string };
  werkzoeken?: PremiumChannelConfig & { endDate: string };
  ictergezocht?: PremiumChannelConfig & { tier: 'basis' | 'premium' };
}

export interface VacancyData {
  quick_note?: string;
  quick_note_author?: string;
  marketResearch: string;
  marketResearchSummary: string;
  marketResearchInsights: MarketInsight[];
  persona: string; // Keep for backward compatibility or summary
  personas: PersonaConfig[]; // New detailed structure
  personaConfig?: PersonaConfig; // New detailed structure
  marketingConfig?: MarketingConfig; // New detailed structure
  personaSummary: string;
  booleanString: string; // Depecrated but kept for history
  sourcingStrategies?: SourcingStrategy[]; // New AI Sourcing
  marketingType: 'Push' | 'Pull' | 'Both';
  marketingStrategy: string;
  massMarketingAdvice: string;
  platforms: any[];
  outreachSequence: any[];
  variations: string[];
  abTestVariants?: ABTestVariant[];
  abTestStatus?: 'draft' | 'running' | 'completed';
  activePlatforms?: {
    careerSite: boolean;
    linkedin: boolean;
    indeed: boolean;
    instagram: boolean;
  };
  tone: VacancyTone;
  extraInstructions: string;
  toneAndSeo: string;
  templates: string;
  structuredTemplates?: VacancyTemplates;
  verificationReport: string;
  finalVacancyContent: string;
  builtVacancySections?: { title: string; content: string }[]; // New Vacancy Builder output
  recruiterNotes: string;
  isVerifiedByRecruiter: boolean;
  publishDate?: string;
  publishedToSite?: boolean;
  lifecycleStatus?: 'Concept' | 'Open' | 'Gesloten' | 'Archief';
  isClosed?: boolean; // Legacy but might be kept for backward compatibility if needed, though lifecycleStatus is better.
  reflections: {
    ai: string;
    human: string;
  };
  leads: any[];
  customLogo?: string;
  themeColor?: string;
  assignedRecruiterId?: string;
  claimStatus?: 'Open' | 'Pending' | 'Assigned';
  serviceLevel?: 'Full' | 'Assisted' | 'Automated';
  tags?: string[];
  category?: string;
  contactPersonId?: string;
  files?: any[]; // Bullhorn or manual files attached to vacancy
  // New Fields
  deadline?: string;
  startDate?: string;
  planningDate?: string; // When the vacancy goes online/active
  closingDate?: string;
  duration?: string;
  contractDuration?: string;
  contractType?: string;
  placementTypes?: string[]; // Vaste dienst, ZZP, IP model
  hoursPerWeek?: string;
  location?: string;
  linkedinSearchString?: string; // New: LinkedIn Search String
  metaAdsCopy?: string; // New: Meta Ads Copy
  // New Creation Flow Fields
  jobCode?: string;
  workplaceType?: 'On-site' | 'Hybrid' | 'Remote';
  salary?: {
    min: number;
    max: number;
    currency: string;
    period: string; // 'Yearly', 'Monthly', 'Hourly'
  };
  experience?: string; // e.g. 'Entry', 'Mid', 'Senior'
  education?: string; // e.g. 'Bachelor', 'Master'
  benefits?: string; // Rich text or simple text
  organizationText?: string; // Organization description
  suggestions?: string; // Requirements / Suggestions
  opdrachtgever_id?: string;
  clientName?: string;
  opdrachtgeverLogoUrl?: string;
  // Werfplaats Flow
  werfplaatsConfig?: WerfplaatsConfig;
  hiringPlan?: HiringPlan;
  premiumJobboards?: PremiumJobboardsData;
  urgencyLevel?: 'low' | 'medium' | 'high';
  campaignChoice?: 'werfplaats' | 'zelf_doen';
  werfplaatsTeamConfig?: { leads: number; selection: string; interview: string; support: string };
  /** Intake checkpoints: diff summaries + snapshot for restore (see utils/vacancyIntakeVersions) */
  intakeVersionHistory?: VacancyIntakeVersionEntry[];
  flowStep?: string;
  /** Audit trail: step visits and intake saves (see utils/vacancyFlowHistory) */
  flowStepHistory?: VacancyFlowStepHistoryEntry[];
}



export enum VacancyStep {
  INTAKE = 'Intake',
  RESEARCH = 'Research',
  PERSONA = 'Persona',
  STRATEGY = 'Strategy', // New intermediate step
  SOURCING = 'Boolean', // Maps to DB enum 'Boolean' but labeled "AI Sourcing" in UI
  COMM_TEMPLATES = 'Templates',
  // Steps removed per user request: TONE_SEO, AI_VERIFICATION, VACANCY_FINAL
  // Marketing Phase
  PUT_LIVE = 'Put_Live',
  PREMIUM_JOB_BOARDS = 'Premium_Job_Boards',
  SHARE_NETWORK = 'Share_Network',
  SHARE_WEBSITE = 'Share_Website',

  // Hiring Phase
  SCREEN_TALENT_POOL = 'Screen_Talent_Pool',
  OUTREACH_1on1 = 'Outreach',
  LIVE_AB = 'Live_AB',
  OPTIMIZATION = 'Optimization',
  LEADGEN_SPLIT = 'Leadgen',
  SHORTLIST = 'Shortlist',
  INTERVIEW_KANBAN = 'Interviews'
}

export type VacancyTone = 'Professional' | 'Friendly' | 'Creative' | 'Direct';

export interface Vacancy {
  id: string;
  clientId: string; // References Client.id
  title: string;
  status: VacancyStep;
  data: Partial<VacancyData>;
  createdAt: string;
  updatedAt: string;

  assignedRecruiterId?: string | null;
  publishedToSite?: boolean;
  shared_with_clients?: string[];
  shared_with_users?: string[];
  description: string;
  responsibilities: Record<string, any>;
  client?: Client;
  eindklant?: Client;
  eindklant_id?: string;
  opdrachtgever_id?: string;
  vacancy_number?: number;
  bullhorn_id?: string;
}

export enum Responsibility {
  WERKPLAATS = 'Werkplaats',
  CLIENT = 'Klant',
  SHARED = 'Gedeeld'
}

export const BULLHORN_STATUS_MAP: Record<string, string> = {
    // New exact user requests
    'Op shortlist': 'Shortlist',
    'Voorstellen': 'Shortlist',
    'Voorgesteld': 'Voorgesteld',
    'Gesprek': 'Gesprek',
    '1e gesprek': 'Gesprek',
    '2e gesprek': 'Gesprek',
    '3e gesprek': 'Gesprek',
    'Aanbod': 'Aanbod',
    'Bevestigd': 'Plaatsing',
    'Plaatsing': 'Plaatsing',
    'Afgewezen': 'Afgewezen',
    'Nog af te wijzen': 'Afgewezen',
    'Automatisch afwijzen': 'Afgewezen',
    
    // Legacy fallbacks to prevent breaking existing data
    'Nieuw': 'Shortlist',
    'New': 'Shortlist',
    'Shortlisted': 'Shortlist',
    'Sourced': 'Shortlist',
    'Client Submission': 'Voorgesteld',
    'Positief klant': 'Voorgesteld',
    'Gespreksafspraken': 'Gesprek',
    'Interviewing': 'Gesprek',
    'Interview 1': 'Gesprek',
    'Interview 2': 'Gesprek',
    'Aanbieding': 'Aanbod',
    'Offer': 'Aanbod',
    'Hired': 'Plaatsing',
    'Rejected': 'Afgewezen',
    'not af te wijzen': 'Afgewezen',
    'Negatief klant': 'Afgewezen',
    'Zelf teruggetrokken': 'Afgewezen',
    'Afgewezen door opdrachtgever': 'Afgewezen',
};

export const BULLHORN_STATUS_OPTIONS = [
    'Op shortlist',
    'Voorstellen',
    'Voorgesteld',
    'Gesprek',
    '1e gesprek',
    '2e gesprek',
    '3e gesprek',
    'Aanbod',
    'Bevestigd',
    'Plaatsing',
    'Afgewezen',
    'Nog af te wijzen',
    'Automatisch afwijzen'
];

export interface CandidateReview {
  id: string;
  vacancyId: string;
  candidateId: string;
  reviewerName: string;
  reviewerEmail: string;
  score: number;
  comment: string;
  createdAt: string;
}

export interface Candidate {
  id: string;
  name: string;
  email: string;
  phone?: string;
  linkedinUrl?: string;
  candidate_number?: number;
  bullhorn_id?: string;
  globalLastContactAt?: string;
  role?: string;
  experience?: number;
  status?: string;
  tags?: string[];
  addedAt?: string;
  reviews?: CandidateReview[];
  photo_url?: string;
  avatar_url?: string;
  cvSummary?: string;
  address_city?: string;
  address_zip?: string;
  address_country?: string;
  source?: string;
  employment_preference?: string[];
  skill_set?: string[];
  original_cv_text?: string;
  ai_parsed_data?: any;
  client_id?: string;
}

export interface PoolCategory {
  id: string;
  clientId: string;
  name: string;
  status: 'active' | 'inactive';
}

export interface PoolTag {
  id: string;
  clientId: string;
  categoryId: string;
  name: string;
  status: 'active' | 'inactive';
}

export interface SubscriptionPlan {
  id: string;
  name: string;
  price: string;
  features: string[];
  vacancyLimit: number;
}

export interface TeamMember {
  id: string;
  name: string;
  role: string;
  email: string;
  joinedAt: string;
}

export interface SupportRequest {
  id: string;
  clientId: string;
  clientName: string;
  subject: string;
  message: string;
  timestamp: string;
  status: string;
  priority: 'Low' | 'Medium' | 'High';
}

export interface RecruiterTask {
  id: string;
  title: string;
  deadline: string;
  completed: boolean;
  priority: 'Low' | 'Medium' | 'High';
  slaStatus?: SLAStatus;
  vacancyId?: string;
  clientName?: string;
}

export interface KanbanCandidate {
  id: string;
  candidateId: string;
  name: string;
  role: string;
  email?: string;
  phone?: string;
  vacancyTitle?: string;
  companyName?: string;
  bullhornId?: string;
  candidateNumber?: number | string;
  status: string;
  addedAt: string;
  addedAtRaw?: number;
  matchScore?: number;
  cvSummary?: string;
  isDeleted?: boolean;
  sourceClient?: string;
  logoUrl?: string | null;
  pipelineMeta?: Record<string, unknown>;
  /** Recruiter: korte hint als klant via portaal akkoord gaf (bv. interview). */
  clientConsentBadge?: string | null;
  portalBlockDetail?: boolean;
  vacancyId?: string;
  defaultClientEmail?: string;
}

export interface KanbanColumn {
  id: string;
  title: string;
  candidates: KanbanCandidate[];
}

export interface LogEntry {
  id: string;
  timestamp: string;
  actor: string;
  actorType: 'AI' | 'HUMAN' | 'SYSTEM';
  action: string;
  entity: string;
  severity: 'INFO' | 'WARNING' | 'CRITICAL';
}

// --- CUSTOM INTERVIEW FLOW & TODOS ---

export interface VacancyStage {
  id: string;
  vacancyId?: string;
  name: string;
  type: 'Screening' | 'Interview' | 'Offer' | 'Hired' | 'Rejected';
  sortOrder: number;
  requirements?: {
    scorecard?: boolean;
    interviewers?: number;
  };
}

export interface Interview {
  id: string;
  vacancyId: string;
  stageId: string;
  candidateId: string;
  scheduledAt: string;
  durationMinutes: number;
  location?: string;
  videoLink?: string;
  interviewers: string[]; // user ids or emails
  status: 'Scheduled' | 'Completed' | 'Cancelled' | 'NoShow';
  candidateName?: string; // aggregated
}

export interface ScorecardCriterion {
  id: string;
  category: string;
  weight: number; // 1-5
  questions: string[];
}

export interface FeedbackScorecard {
  id: string;
  stageId: string;
  criteria: ScorecardCriterion[];
}

export interface CandidateFeedback {
  id: string;
  interviewId: string;
  reviewerId: string;
  reviewerName?: string;
  scores: Record<string, number>; // criteria_id -> score (1-5)
  overallScore?: number;
  notes?: string;
  recommendation?: 'Hire' | 'Strong Hire' | 'No Hire' | 'Maybe';
  createdAt: string;
}

export interface Todo {
  id: string;
  title?: string;
  description: string;
  due_date?: string;
  dueDate?: string;
  status: 'Pending' | 'In Progress' | 'Completed';
  priority: 'Low' | 'Medium' | 'High';
  userId?: string;
  vacancyId?: string;
  candidateId?: string;
  is_external?: boolean;
  isExternal: boolean;
  externalAssigneeEmail?: string;
  attachments?: string[];
  context?: {
    vacancyTitle?: string;
    candidateName?: string;
  };
  createdAt: string;
}

export interface WerfplaatsConfig {
  leadsCount: number;
  topSelection: boolean;
  topSelectionCount: number;
  interviewPlanning: 'none' | 'light' | 'full';
  vacancySupport: 'none' | 'help' | 'manage';
  totalPrice: number;
  currency: string;
  selectedTime?: string | null;
}

export interface HiringPlan {
  timeline: {
    start: string;
    deadline: string;
    urgency: 'Low' | 'Medium' | 'High';
  };
  stages: string[];
  team: {
    manager: string;
    recruiters: string[];
    interviewers: string[];
  };
}

export interface Placement {
    id: string;
    client_id: string;
    number: string;
    status: string;
    source: string;
    candidate_id: string;
    placement_type: string;
    recruiter_id: string;
    candidate_source: string;
    vacancy_id: string;
    end_client: string;
    contract_signed: boolean;
    start_date: string;
    end_date: string;
    employment_type: string;
    contract_type: string;
    billing_rate: number;
    salary: number;
    pay_rate: number;
    data: any;
    created_at: string;
    updated_at: string;
    candidate?: any;
    vacancy?: any;
    recruiter?: any;
    resolved_opdrachtgever?: any;
    resolved_eindklant?: any;
}

